/**
 * Sidebar Component
 *
 * A collapsible sidebar for navigation.
 * Supports nested navigation items with expandable groups.
 * State persisted via Zustand store.
 * Theme-aware styling using CSS variables.
 * Permission-based filtering when a role session is active.
 */

import { ReactNode, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import clsx from "clsx";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useSidebarStore } from "../../stores/sidebarStore";
import { hasPermission } from "../../lib/utils/permissions";
import type { ActiveRoleDetails } from "../../types/auth.types";

export interface NavChildItem {
  label: string;
  href: string;
  icon: ReactNode;
}

export interface NavItem {
  label: string;
  href?: string;
  icon: ReactNode;
  /** @deprecated Use requiredPermission instead */
  adminOnly?: boolean;
  /** Permission required to see this nav item (e.g., "iam:user:read") */
  requiredPermission?: string;
  children?: NavChildItem[];
}

export interface SidebarProps {
  /** Currently active role session (if any) */
  activeRole?: ActiveRoleDetails | null;
  customNavItems: NavItem[];
}

export const Sidebar = ({ activeRole, customNavItems }: SidebarProps) => {
  const navItems = customNavItems;
  const location = useLocation();
  const { isCollapsed, toggleSidebar } = useSidebarStore();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    // Auto-expand groups that contain the current route
    const expanded = new Set<string>();
    navItems.forEach((item) => {
      if (item.children) {
        const isChildActive = item.children.some((child) => location.pathname.startsWith(child.href));
        if (isChildActive) {
          expanded.add(item.label);
        }
      }
    });
    return expanded;
  });

  // Filter nav items based on active role permissions
  const filteredItems = navItems.filter((item) => {
    // If no requiredPermission, the item is always visible
    if (!item.requiredPermission) {
      return true;
    }

    // If there's no active role, hide items that require permissions
    if (!activeRole) {
      return false;
    }

    // Check if the active role has the required permission
    return hasPermission(activeRole.permissions, item.requiredPermission);
  });

  const toggleGroup = (label: string) => {
    if (isCollapsed) return; // Don't toggle groups when collapsed
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const isGroupActive = (item: NavItem): boolean => {
    if (!item.children) return false;
    return item.children.some((child) => location.pathname.startsWith(child.href));
  };

  return (
    <aside
      className={clsx("flex-shrink-0 border-r transition-all duration-300 ease-in-out", isCollapsed ? "w-16" : "w-64")}
      style={{
        backgroundColor: "var(--color-sidebar-bg)",
        borderColor: "rgba(255, 255, 255, 0.1)",
      }}
    >
      <div className="flex flex-col h-full">
        {/* Collapse toggle button */}
        <div
          className={clsx("flex items-center h-12", isCollapsed ? "justify-center px-2" : "justify-end px-3")}
          style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
        >
          <button
            onClick={toggleSidebar}
            className="p-1.5 rounded-lg transition-colors hover:text-white"
            style={{ color: 'var(--color-sidebar-text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-sidebar-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? <ChevronRightIcon className="h-5 w-5" /> : <ChevronLeftIcon className="h-5 w-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto overflow-x-hidden" style={{ color: 'var(--color-sidebar-text)' }}>
          {filteredItems.map((item, index) => {
            const prevItem = index > 0 ? filteredItems[index - 1] : null;
            const showDivider = prevItem && (
              (prevItem.children && !item.children) ||
              (!prevItem.children && item.children)
            );

            // Item with children (expandable group)
            if (item.children) {
              const isExpanded = expandedGroups.has(item.label) && !isCollapsed;
              const isActive = isGroupActive(item);

              return (
                <div key={item.label}>
                  {showDivider && (
                    <div className={clsx(
                      "mx-3 my-2 border-t",
                      "border-white/10"
                    )} />
                  )}
                  {/* Group header */}
                  <div className="relative group">
                    <button
                      onClick={() => toggleGroup(item.label)}
                      className={clsx(
                        "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        isActive
                          ? "theme-sidebar-item-active"
                          : "hover:bg-white/10",
                        isCollapsed && "justify-center px-2"
                      )}
                    >
                      <span className={clsx("flex-shrink-0", isActive ? "text-inherit" : "text-white/50")}>
                        {item.icon}
                      </span>
                      {!isCollapsed && (
                        <>
                          <span className="flex-1 text-left">{item.label}</span>
                          <ChevronDownIcon className={clsx("h-4 w-4 transition-transform", isExpanded && "rotate-180")} />
                        </>
                      )}
                    </button>
                    {isCollapsed && (
                      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2.5 py-1.5 rounded-md text-xs font-medium bg-gray-900 text-white dark:bg-gray-700 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 whitespace-nowrap z-50 shadow-lg">
                        {item.label}
                        <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-700" />
                      </div>
                    )}
                  </div>

                  {/* Children */}
                  {!isCollapsed && (
                    <div className={clsx(
                      "grid transition-[grid-template-rows] duration-200 ease-in-out",
                      isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    )}>
                      <div className="overflow-hidden">
                        <div className="ml-4 mt-1 space-y-1">
                          {item.children.map((child) => (
                            <NavLink
                              key={child.href}
                              to={child.href}
                              className={({ isActive: childActive }) =>
                                clsx(
                                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                                  childActive
                                    ? "theme-sidebar-item-active font-medium"
                                    : "hover:bg-white/10"
                                )
                              }
                            >
                              {({ isActive: childActive }) => (
                                <>
                                  <span
                                    className={clsx(
                                      "flex-shrink-0",
                                      childActive ? "text-inherit" : "text-white/50"
                                    )}
                                  >
                                    {child.icon}
                                  </span>
                                  {child.label}
                                </>
                              )}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // Regular item (no children)
            return (
              <div key={item.href} className="relative group">
                {showDivider && (
                  <div className={clsx(
                    "mx-3 my-2 border-t",
                    "border-white/10"
                  )} />
                )}
                <NavLink
                  to={item.href!}
                  className={({ isActive }) =>
                    clsx(
                      "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      isActive
                        ? "theme-sidebar-item-active"
                        : "hover:bg-white/10",
                      isCollapsed && "justify-center px-2"
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={clsx("flex-shrink-0", isActive ? "text-inherit" : "text-white/50")}>
                        {item.icon}
                      </span>
                      {!isCollapsed && <span className="flex-1">{item.label}</span>}
                    </>
                  )}
                </NavLink>
                {isCollapsed && (
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2.5 py-1.5 rounded-md text-xs font-medium bg-gray-900 text-white dark:bg-gray-700 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 whitespace-nowrap z-50 shadow-lg">
                    {item.label}
                    <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-700" />
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
